import { useState } from 'react';
import { useT } from '../i18n/useLanguage';
import PlaceholderImage from '../components/PlaceholderImage';
import Reveal from '../components/Reveal';
import type { MessageKey } from '../i18n/messages';

// Per Figma 1:4097 the design ships a 4×3 = 12-cell roster. Each
// entry carries its MBTI code (universal — not localised), and an
// i18n key for the localised display name plus an i18n key for the
// chat-bubble line shown in the featured-character column when this
// entry is active. Names + bubbles live in `i18n/messages.ts` under
// `staff.name.0…11` and `staff.bubble.0…11` for both Thai and
// English. The 12th slot is a "many more staff!" placeholder with
// no MBTI code.
type StaffEntry = {
  /** MBTI code, or '-' for the "many more staff" placeholder slot. */
  mbti: string;
  /** i18n key for the staff member's display name. */
  nameKey: MessageKey;
  /** i18n key for the chat-bubble line shown when this entry is
   *  the active roster cell. */
  speechKey: MessageKey;
  /** Full-body character art shown in the featured column when
   *  this entry is the active roster cell. */
  fullSrc: string;
};

// Mock sprite reused by every staff entry until per-character art
// is available. Defined once at module scope so swapping it later
// (or driving it per-entry in the array) is a single-line change.

const STAFF: StaffEntry[] = [
  { mbti: 'ISTJ', nameKey: 'staff.name.0', speechKey: 'staff.bubble.0', fullSrc: 'images/staff/staff-01.png' },
  { mbti: 'ISTJ', nameKey: 'staff.name.1', speechKey: 'staff.bubble.1', fullSrc: 'images/staff/staff-02.png' },
  { mbti: 'ISFJ', nameKey: 'staff.name.2', speechKey: 'staff.bubble.2', fullSrc: 'images/staff/staff-03.png' },
  { mbti: 'ENFP', nameKey: 'staff.name.3', speechKey: 'staff.bubble.3', fullSrc: 'images/staff/staff-04.png' },
  { mbti: 'ESFP', nameKey: 'staff.name.4', speechKey: 'staff.bubble.4', fullSrc: 'images/staff/staff-05.png' },
  { mbti: 'ENTJ', nameKey: 'staff.name.5', speechKey: 'staff.bubble.5', fullSrc: 'images/staff/staff-06.png' },
  { mbti: 'ESTJ', nameKey: 'staff.name.6', speechKey: 'staff.bubble.6', fullSrc: 'images/staff/staff-07.png' },
  { mbti: 'INFP', nameKey: 'staff.name.7', speechKey: 'staff.bubble.7', fullSrc: 'images/staff/staff-08.png' },
  { mbti: 'ENFP', nameKey: 'staff.name.8', speechKey: 'staff.bubble.8', fullSrc: 'images/staff/staff-09.png' },
  { mbti: 'ENFJ', nameKey: 'staff.name.9', speechKey: 'staff.bubble.9', fullSrc: 'images/staff/staff-10.png' },
  { mbti: 'INFP', nameKey: 'staff.name.10', speechKey: 'staff.bubble.10', fullSrc: 'images/staff/staff-11.png' },
  { mbti: '-',    nameKey: 'staff.name.11', speechKey: 'staff.bubble.11', fullSrc: 'images/staff/staff-12.png' },
];

const STAFF_COUNT = STAFF.length;

export default function StaffSection() {
  const t = useT();
  const [active, setActive] = useState(0);
  const current = STAFF[active];

  return (
    <section
      id="staff"
      // z-20 + relative makes this section its own stacking context.
      // Without it, the bg image's -bottom-[20px] bleed into the next
      // section (Campaign) gets painted OVER by Campaign because
      // Campaign comes after Staff in DOM order — same z, later
      // wins. Promoting Staff to z-20 (above the z-10 outer panels
      // used by sibling sections and above Campaign's z-auto = 0)
      // makes the bottom border + rounded bottom corners render on
      // top of Campaign's content in the overlap strip, the same way
      // the top bleed already covers FeaturesSection above.
      //
      // Mobile-specific scroll behaviour (mirrors Register / Story
      // / Features / Campaign mobile):
      //   • `!min-h-screen` (important) forces the section to
      //     ≥ 100vh on phones. The `!` is required because
      //     `.snap-section { min-height: 80vh }` is declared
      //     outside `@layer` in index.css after `@tailwind
      //     utilities`, so a plain `min-h-screen` would lose on
      //     source-order.
      //   • `justify-center` only applies at md+ (`md:
      //     justify-center`); on mobile the section uses no
      //     vertical justify, so the inner Reveal can hug the
      //     top of the section once mandatory snap pins it.
      //   • The inner Reveal below carries `snap-start
      //     [scroll-margin-top:60px]` so when mandatory snap
      //     settles on mobile the Reveal's top lands 60px below
      //     the viewport top — just under the ≈48px floating
      //     header — and "character column → pinboard frame"
      //     stacks downward in the flex-col-reverse layout.
      className="snap-section relative z-20 flex md:max-h-screen lg:max-h-none !min-h-screen flex-col items-center bg-section-staff text-text-inverse md:justify-center"
    >
      {/* Section background — section5-bg.png (4050×3020, ≈1.34:1)
          painted as a rounded card that EXTENDS ABOVE the section's
          natural top so it overlaps the bottom of FeaturesSection,
          letting the curtained-room scene bleed into the previous
          section.

          How the overlap works:
            • The parent <section> no longer carries `overflow-hidden`
              — that was the clip rect anchoring the bg image to
              the section's visible bounds. Without it, the absolute
              <img> below is free to extend past either edge.
            • `-top-[120px]` pulls the image's top edge 120px above
              the section's natural top, so the rounded top corners
              and the upper portion of the image render INTO the
              FeaturesSection's bottom strip.
            • `bottom-0` keeps the bottom edge aligned with the
              section's bottom (i.e. no overlap into the next
              section — easy to extend if you want a matching
              bleed downward).
            • The implicit height (`100% + 120px`) keeps the image
              filling section + the upward bleed, and `object-cover`
              preserves the room composition by cropping rather
              than stretching.
            • Default stacking (no explicit z): the image renders
              into FeaturesSection's bottom strip via DOM order —
              <main>'s children paint in order, so this section's
              children paint AFTER Features'. FeaturesSection's
              own `overflow-hidden` keeps its content (z-10 panel,
              file folder, etc.) clipped to its own bounds, so
              the overlap region is mostly Features' bg image,
              which this image cleanly covers.
            • `rounded-3xl` keeps the rounded card silhouette
              visible at all four corners — the top two now sit
              up inside Features' territory. */}
      {/* Why height is set explicitly via calc() instead of relying
          on the (-top -20, -bottom -20) anchor pair to imply it:
          <img> has an intrinsic aspect ratio, and some browsers will
          prefer that intrinsic height over the CSS-computed
          top/bottom-derived height for absolutely-positioned replaced
          elements. On tall/portrait viewports that fallback rendered
          the image at `section_width / 1.34` — much shorter than the
          section — leaving empty bands at the top and bottom even
          with `object-cover`. Pinning `h-[calc(100%+40px)]` makes the
          height non-negotiable so the image always fills section +
          the 20px upper/lower bleed, and `object-cover` crops cleanly
          to fit whatever aspect the section ends up at. */}
      <img
        src="/images/section5-bg.png"
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        className="hidden lg:block border-t-4 border-b-4 border-white pointer-events-none absolute -top-[20px] h-[calc(100%+40px)] w-full select-none rounded-3xl object-cover object-[center_100%] scale-[1]"
        draggable={false}
      />
      <img
        src="/images/section5-bg-mobile.png"
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        className="lg:hidden border-t-4 border-b-4 border-white pointer-events-none absolute -top-[20px] inset-x-0 h-[calc(100%+40px)] w-full select-none rounded-3xl object-cover"
        draggable={false}
      />

      {/* Outer content panel — same envelope as FeaturesSection so
          the campaign scenes share an identical content shell.
          Pinned to `min-h-screen` on EVERY breakpoint so the inner
          children (title pill / Reveal / character column) have a
          concrete 100vh canvas to distribute against with flex-1.
          Without an explicit height on this wrapper, `flex-1`
          inside the Reveal on mobile collapses to content height
          and the "frame ≥ 45vh / character column fills the rest"
          rule below would silently fail.
          Mobile keeps `justify-start` so the snap-targeted Reveal
          stacks from the top under the header; md+ centres the
          content the same way it always did. */}
      <div className="lg:w-[100%] relative z-10 flex min-h-screen w-full max-w-[1874px] flex-col items-center justify-start md:justify-center md:px-8 md:py-12">
        {/* Inner Reveal — flex container that puts the frame and the
            featured-character column side by side on md+. Mobile
            stacks (flex-col-reverse) so the character column
            appears ABOVE the pinboard frame.
            `snap-start [scroll-margin-top:60px]` makes this Reveal
            the mobile section's snap target (mirrors Register /
            Story / Features / Campaign mobile). When mandatory
            snap settles on mobile the Reveal's top lands 60px
            below the viewport top — just below the floating
            header — and the character + pinboard stack downward
            naturally from there. Harmless on md+ where
            scroll-snap is disabled at <html> level. */}
        {/* Mobile-only title pill — Figma 208:146 lifts the
            section title OUT of the rose-pink pinboard frame's
            tab area and parks it at the top of the mobile
            layout, above the featured-character column. The
            translucent `#FFFFFF80` (rgba(255,255,255,0.5))
            `rounded-[50px]` lozenge matches
            Figma 208:147 (the half-transparent rounded
            background that the curtained-room scene reads
            through). Three rows inside the lozenge mirror the
            desktop title-inside-frame block:
              1. h2 — "พนักงาน สุดยูนีค" with the white +
                 #FFC0D1 accent split.
              2. p  — "มากกว่า 40+ คน พร้อมเข้าร้าน!" subtitle.
              3. div — fork-spoon-flanked tan divider bar.
            All three reuse the same i18n keys + assets the
            desktop branch uses, so language toggling and copy
            edits flow to both branches from one source.
            Hidden at md+ — the desktop title-inside-frame
            branch above takes over there. */}
        {/* `max-h-[20vh] shrink-0` caps the title pill at 20% of
            the viewport on mobile so it can never eat more than a
            fifth of the screen — the rest is reserved for the
            featured-character column (flex-1) and the pinboard
            frame (min-h-[45vh]). `shrink-0` is the safety belt:
            once the parent (outer panel) is `min-h-screen` and
            the Reveal grabs the rest with `flex-1`, this pill
            would otherwise be eligible to shrink under flex
            pressure; pinning it disables that. */}
        <div
          className="mx-auto mb-4 mt-8 flex max-h-[14vh] w-[92%] max-w-md shrink-0 flex-col py-1 items-center gap-2 rounded-[30px] md:hidden"
          style={{ background: 'rgba(255, 255, 255, 0.50)' }}
        >
          <h2 className="staff-title-shadow whitespace-nowrap text-center font-display text-4xl sm:text-5xl font-extrabold text-white">
            {t('staff.title')}
            <span className="staff-title-shadow-accent ms-1 text-[0.78em]">
              {t('staff.titleAccent')}
            </span>
          </h2>
          <p className="text-center font-thai text-xl font-bold text-section-feature mt-1">
            {t('staff.subtitle')}
          </p>
          {/* <div className="flex w-[80%] items-center justify-center gap-2">
            <img
              src="/images/fork-spoon.svg"
              alt=""
              aria-hidden="true"
              className="h-4 w-4"
              draggable={false}
            />
            <div className="h-[2px] flex-1 rounded-full bg-tan" />
            <img
              src="/images/fork-spoon.svg"
              alt=""
              aria-hidden="true"
              className="h-4 w-4"
              draggable={false}
            />
          </div> */}
        </div>

        {/* Mobile: `flex-1 min-h-0` makes the Reveal swallow the
            outer panel's leftover vertical space (panel is now
            `min-h-screen`, title pill is capped at 20vh) so the
            two children below can split that space:
              • Frame wrapper (next div) — `min-h-[45vh]`, sits at
                the BOTTOM of the column-reverse stack.
              • Featured-character column — `flex-1`, fills
                whatever's left between the title and the frame.
            `min-h-0` is the standard flex-item escape hatch so
            the Reveal can shrink below its intrinsic content
            height when needed (default `min-height: auto` on flex
            items would otherwise refuse to shrink past the
            character column's natural height and break the
            distribution).
            `md:flex-initial` reverts to the original behaviour at
            md+ where the Reveal is just a flex-row of two
            content-sized children. */}
        <Reveal className="relative flex w-full overflow-hidden max-w-[1239px] flex-1 min-h-[32vh] flex-col-reverse items-center gap-6 snap-start [scroll-margin-top:60px] md:flex-initial md:max-w-none md:flex-row md:items-end md:justify-start md:gap-4 lg:gap-8">
          {/* Inner frame — uses section5-frame.png as the rose-pink
              pinboard-style background (native 1907×1519, ≈1.255:1).
              Aspect-locked to that ratio so the painted border lines
              up at every size, capped at `max-h-[85vh]` so short
              laptops shrink the frame's width via the aspect-ratio +
              vh cap (matches StorySection's height behaviour) rather
              than bursting past the viewport. Width cap kept at
              1239px so this section aligns on the same column as
              Features. */}
          {/* Mobile-only "frame area" wrapper.
              Why: rule 1 says the pinboard frame is the marquee
              view on mobile and must occupy AT LEAST 45vh of the
              section. We can't just put `min-h-[45vh]` on the
              frame box itself because the frame's interior (the
              12-cell staff grid, the title block, the lace strip)
              is absolutely-positioned in PERCENTAGES of the box.
              If the box is forced taller than its natural
              aspect-driven height, the painted pinboard art (a
              background-image with `bg-contain bg-bottom`) stays
              at its natural aspect and anchors to the bottom,
              while the grid percentages get computed against the
              taller box — they'd drift up and stop landing on the
              painted cells.
              Solution: keep the frame box ITSELF at its natural
              `aspect-[668/467]` (rule 1: w-full + h-auto from
              aspect — preserves grid alignment) and put the
              `min-h-[45vh]` constraint on THIS wrapper instead.
              `flex flex-col justify-end` then pins the frame box
              to the bottom of the wrapper, leaving any excess
              45vh-minus-natural-height as empty space ABOVE the
              frame. Net effect: the frame area is always ≥ 45vh,
              the frame's painted chrome + staff grid stay
              pixel-aligned, and the character column above gets
              proportionally less room without overlapping the
              frame.
              `md:contents` neutralises the wrapper at md+ —
              `display: contents` removes its box from the layout
              tree, so the inner frame div behaves as a direct
              child of the Reveal flex-row (carrying its own
              `md:w-[60%] md:self-center` etc. unchanged). */}
          <div className="flex w-full w-full flex-1 min-h-0 flex-col justify-end md:contents">
            {/* Mobile-only "selected staff" card.
                Mirrors the desktop character column (speech bubble
                on top, full character portrait on a soft spotlight
                stage in the middle, MBTI + name pill row pinned to
                the bottom) but lives INSIDE the frame wrapper so
                it occupies the empty space ABOVE the pinboard
                frame on phones.
                Height rule: `flex-1 min-h-0` makes this panel
                consume EXACTLY the wrapper's leftover vertical
                space — i.e. (wrapper area − inner frame's natural
                aspect-driven height). The frame below has no
                flex-grow, so it stays at its natural height and
                this panel takes the rest. `min-h-0` is the flex
                escape hatch so the panel can also shrink below
                its content height when the wrapper is short,
                guaranteeing it never pushes past the frame.
                `md:hidden` keeps it off desktop — the dedicated
                character column to the right of the frame
                renders there instead. `key={active}` re-mounts
                the card on selection change so any future
                enter-transition can replay. */}
            <div
              key={active}
              className="relative flex max-h-[min(100vh,512px)] flex-1 flex-col items-center justify-center gap-2 px-4 pb-2 md:hidden"
            >
              {/* Character + spotlight stage. `flex-1 min-h-0` lets
                  this stage swallow whatever vertical room the
                  MBTI/name row doesn't claim, and `min-h-0` again
                  lets it shrink past the portrait's intrinsic size
                  on short phones. The portrait uses `h-full w-auto
                  object-contain` so it always scales to fit this
                  stage without cropping or overflowing.
                  The speech bubble lives INSIDE this stage now
                  (absolutely positioned at the top-left) so it
                  physically sticks to the stage's top edge instead
                  of being a separate flex sibling above it — that
                  way the bubble can never drift away from the
                  portrait as the stage resizes, and the gap-2 on
                  the parent no longer wedges whitespace between
                  the bubble and the character's head. */}
              <div className="hiddenrelative flex w-full h-full flex-1 min-h-[41vh] items-end justify-center">
                {/* Speech bubble — same asset (staff-chat-bubble.png)
                    as the desktop column, with the rounded card +
                    tail baked into the PNG. `absolute top-0 left-0`
                    pins it to the stage's top-left corner; `z-10`
                    keeps it above the spotlight circles AND the
                    character portrait (which is z-[1]) so the
                    bubble visually overlaps the character's head
                    the same way the desktop layout does via the
                    old negative-margin trick. `max-w-[90%]`
                    prevents the bubble from running off the right
                    edge of the stage when the speech text is long. */}
                <div
                  // `min-h-[2lh]` reserves exactly two line-heights of
                  // vertical space so the bubble's height is stable
                  // across all characters' speech lines — a one-line
                  // line still occupies the same bubble as a two-line
                  // one, and `items-center` vertically centres the
                  // text inside that fixed 2-line slot.
                  // `leading-snug` pins the line-height so the `lh`
                  // unit resolves consistently regardless of inherited
                  // line-height up the tree.
                  className="absolute bottom-[calc(min(32vh,100vw))] left-[10%] sm:left-[20%] z-10 inline-flex min-h-[2lh] max-w-[90%] items-center justify-center whitespace-pre-line px-6 py-3 text-center font-thai text-xs font-bold leading-snug text-section-feature"
                  style={{
                    backgroundImage: "url('/images/staff-chat-bubble.png')",
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                  }}
                >
                  {t(current.speechKey)}
                </div>
                <div
                  aria-hidden="true"
                  className="absolute left-1/2 top-[5%] -z-0 aspect-square w-[60%] -translate-x-1/2 rounded-full"
                />
                <div
                  aria-hidden="true"
                  className="absolute top-[5%] left-1/2 -z-0 h-[10%] w-[45%] -translate-x-1/2 rounded-full bg-section-feature/20 blur-[2px]"
                />
                <img
                  src={'/images/staff/staff-bg-light.png'}
                  className="absolute bottom-[15%] left-0 z-[1] h-[50%] w-full object-contain"
                />
                <div className='relative w-full h-full min-h-[32vh]'>
                  <PlaceholderImage
                    src={current.fullSrc}
                    alt={`Staff ${active + 1} full`}
                    hint={`staff ${active + 1} full`}
                    className="relative mx-auto z-[1] h-[calc(min(32vh,100vw))] w-auto object-contain"
                  />
                </div>
              </div>

              {/* MBTI + Name pill row — identical chrome to the
                  desktop column (Figma 1:4181 + 1:4184) just at
                  mobile font sizes. Anchored to the bottom of the
                  card via the parent's `justify-end`. */}
              <div className="flex w-full items-center justify-center gap-2">
                <span
                  className="rounded-full border-[3px] border-section-feature bg-[#ffb9b9] px-3 py-1 font-display text-xs font-extrabold leading-none text-section-feature"
                  style={{
                    filter:
                      'drop-shadow(0 1px 0 #634645) drop-shadow(0 1px 0 #59220d) drop-shadow(0 1px 0 #59220d)',
                    WebkitTextStroke: '3px #FFFFFF',
                    paintOrder: 'stroke fill',
                  }}
                >
                  {current.mbti}
                </span>
                <span className="flex truncate rounded-full bg-section-feature px-4 py-1.5 text-center font-thai text-xs font-bold text-white">
                  {t(current.nameKey)}
                </span>
              </div>
            </div>

            <div
              // `md:self-center` overrides the Reveal's `md:items-end`
              // so the frame is vertically centred in the flex row
              // instead of bottom-aligned with the character column.
              // The character intentionally stays at `items-end` so its
              // feet sit on the ground line; the frame on the other
              // hand looks better centred against the Reveal's flex
              // line and the section's vertical mid-line.
              //
              // Background image swaps at the md breakpoint:
              //   • mobile (<768px) — staff-items-bg-mobile.png
              //     (cropped/recomposed pinboard art tuned for the
              //     portrait stack on phones)
              //   • md+ — section5-frame.png (desktop pinboard art)
              // bg-contain + bg-center + bg-no-repeat are shared so
              // either asset is letterboxed cleanly inside the box.
              // Sizing model by breakpoint:
              //   • mobile (<md): width-driven (`w-full` + aspect-[668/467]),
              //     capped at max-h-[90vh] so a tall phone doesn't overflow.
              //   • md … xl: width-driven landscape (`w-[60%]` + aspect-[6/5]
              //     → height derived from width). `md:max-h-none` lets it
              //     grow freely.
              //   • 2xl / 4xl: HEIGHT-driven instead — `h-[Nvh]` + `w-auto`
              //     so the aspect-[6/5] now derives WIDTH from a viewport-
              //     relative height. This is the "don't lock the frame to
              //     width" fix: on a short 1920×1080 panel the frame shrinks
              //     to fit the height, and on a taller 1920×1200 panel the
              //     same vh resolves to more pixels so the frame grows into
              //     the spare vertical space. max-w-[1280px] still caps the
              //     derived width. The outer panel stays `min-h-screen`, so
              //     it remains a 100vh floor that expands with the content.
              className="lg:min-h-[44vw] relative w-full item-end justify-end max-w-[1280px] aspect-[668/467] md:aspect-[6/5] max-h-[90vh] md:max-h-none bg-[url(/images/staff-items-bg-mobile.png)] bg-contain bg-bottom bg-right bg-no-repeat md:w-[60%] md:flex-none md:self-center md:bg-[url(/images/section5-frame.png)] 2xl:h-[90vh] 2xl:w-auto 4xl:h-[80vh]"
            >
              
              {/* Header block — centered at the top of the frame so the
                  title sits inside the "tab" bump that the frame asset
                  paints across the top edge. Combines title +
                  subtitle + a fork-spoon divider, all three from
                  Figma 1:4102. */}
              <div className="absolute left-[50%] top-[3%] z-30 hidden w-[70%] -translate-x-1/2 flex-col items-center gap-[1.5%] md:flex">
                {/* Per Figma 1:2469 the title splits into two
                    differently-sized halves on a single line:
                      • "พนักงาน" — white at the base size (Figma:
                        64px). Wears the full staff-title-shadow
                        treatment (white fill, brown stroke,
                        extruded brown drop-shadow tuned via the
                        --staff-stroke-width var ladder).
                      • "สุดยูนีค" — pink #FFC0D1 at ~78% of the
                        base size (Figma: 50px / 64px = 0.781).
                        `text-[0.78em]` keeps the ratio exact at
                        every responsive breakpoint without having
                        to maintain a parallel set of breakpoint
                        classes. The dedicated
                        .staff-title-shadow-accent class swaps the
                        fill to pink and restates the stroke + shadow
                        so the accent half can be tuned independently
                        from the white half if the design diverges. */}
                {/* <h2 className="features-title-shadow whitespace-nowrap text-center font-display text-2xl font-extrabold text-white md:text-4xl lg:text-[48px] 2xl:text-[58px] 2xl:font-black tracking-tighter "> */}
                
                <h2 className="staff-title-shadow mt-2 lg:mt-6 whitespace-nowrap text-center font-display text-2xl font-extrabold text-white md:text-3xl lg:text-[48px] xl:text-[58px] 2xl:text-[64px] ">
                  {t('staff.title')}
                  <span className="staff-title-shadow-accent ms-1 lg:ms-2 text-[0.80em]">
                    {t('staff.titleAccent')}
                  </span>
                </h2>
                <p className="paw-label text-center mt-4 lg:mt-6 xl:mt-10 font-thai text-sm font-bold text-section-feature md:text-base lg:text-xl xl:text-3xl">
                  {t('staff.subtitle')}
                </p>
                <div className="flex w-[60%] max-w-4xl items-center justify-center gap-2 xl:mt-1">
                  <img
                    src="/images/fork-spoon.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-4 w-4 xl:h-8 xl:w-8"
                    draggable={false}
                  />
                  <div className="h-[2px] xl:h-[3px] flex-1 rounded-full bg-tan" />
                  <img
                    src="/images/fork-spoon.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-4 w-4 xl:h-8 xl:w-8"
                    draggable={false}
                  />
                </div>

                <div className={`hidden lg:block absolute aspect-[14/6] h-auto 2xl `
                  + ` w-[120%] me-6 top-[110%]`
                  + ` 2xl:w-[125%] 2xl:left-[-12%] 2xl:me-0 2xl:aspect-[16/8]`
                }
                >
                  <ul className="grid h-full grid-cols-4 grid-rows-3 gap-x-0 gap-y-10 lg:px-10">
                    {STAFF.map((staff, i) => {
                      const isActive = i === active;
                      const iconSrc = `/images/staff-icon-${String(i + 1).padStart(2, '0')}.png`;
                      return (
                        <li key={i} className="flex items-start justify-center">
                          <button
                            type="button"
                            onClick={() => setActive(i)}
                            aria-pressed={isActive}
                            aria-label={`Staff ${i + 1}: ${t(staff.nameKey)}`}
                            className={`group relative flex w-full max-w-[3.5rem] flex-col items-center transition-transform duration-200 ease-out focus:outline-none sm:max-w-[4.5rem] md:max-w-[5.5rem] lg:max-w-[8.5rem] ${
                              isActive ? '' : 'hover:scale-105'
                            }`}
                          >
                            {/* Icon + pill background grouped in one
                                wrapper. The SVG provides the size
                                baseline (intrinsic 126×89 resting /
                                145×94 selected, scaled to cell width);
                                the icon is absolute-positioned over
                                the SVG with its body tucked inside the
                                pill and head sticking up above. Because
                                both children live inside this single
                                wrapper, the active-state `scale-110`
                                transform applies to the pair as a unit
                                — the icon never drifts off the pill
                                when the cell pops up, regardless of
                                cell size. Resting pill = Figma 186:4709
                                (cream→peach gradient). Selected pill =
                                Figma 186:4700 (asymmetric rose-pink). */}
                            <div
                              className={`relative w-full origin-top transition-transform duration-200 ease-out ${
                                isActive ? 'scale-110' : ''
                              }`}
                            >
                              <img
                                src={
                                  isActive
                                    ? '/images/staff-bg-selected.svg'
                                    : '/images/staff-item-bg.svg'
                                }
                                alt=""
                                aria-hidden="true"
                                draggable={false}
                                className="block h-auto w-full select-none"
                              />
                              <PlaceholderImage
                                src={iconSrc}
                                alt={`Staff ${i + 1} portrait`}
                                hint={`#${i + 1}`}
                                className="pointer-events-none absolute left-1/2 bottom-[15%] z-10 h-[85%] w-auto -translate-x-1/2 object-contain"
                              />
                            </div>

                            {/* Name label — selected gets a white
                                rounded-pill background per Figma
                                186:4534, resting is plain centred
                                text. line-clamp-2 keeps long names
                                from pushing the cell out of its row. */}
                            <p
                              className={`hidden paw-label no-wrap min-h-[3.5rem] 2xl:block mt-0 line-clamp-2 max-w-full text-center font-thai text-[0.6rem] font-bold leading-tight transition md:mt-0 md:text-xs lg:text-lg ${
                                isActive
                                  ? 'rounded-full text-section-feature shadow-sm'
                                  : 'text-section-feature'
                              } px-2 py-0.5 md:px-3 md:py-1`}
                            >
                              {t(staff.nameKey)}
                            </p>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* Roster — 4 columns × 3 rows = 12 staff cells per
                  Figma 186:4500-4540. Each cell composes:
                    • A character portrait (staff-icon-XX.png,
                      downloaded from Figma) that overlaps the top of
                      the pill from above — head sticks out, body
                      tucked inside the pill.
                    • A horizontally-elongated lozenge "pill" with a
                      cream→peach gradient (#ffdab5 → #ffebda),
                      a 4px white border, an outer drop shadow, and
                      an inset pink shadow at the bottom edge.
                    • A name label below the pill in section-feature
                      brown.

                  Active cell ("selected") swaps the pill chrome to
                  the Figma 186:4700 styling: rose-pink gradient
                  (#f9a3a3 → #ffebda) with a darker #ffc7c1 border,
                  bottom-right corner sharper than the others
                  (asymmetric radius), a stronger drop shadow, and a
                  white rounded-pill background under the name label
                  (Figma 186:4534). The cell also lifts up a bit via
                  `-translate-y-2` so it visibly pops above the
                  resting row.

                  Why insets stayed:
                    • The grid wrapper still rides at
                      `inset-x-[8%] top-[32%] bottom-[14%]` so it
                      aligns inside the section5-frame's cream
                      diamond-pattern interior. */}
              <div className="lg:hidden absolute 2xl:aspect-[14/8] 2xl:left-none 2xl:right-0 2xl:mx-auto 2xl:w-[75%] top-[10%] md:top-[28%] sm:top-[16%] bottom-[16%] md:bottom-[16%] z-20 px-2 py-4 left-[10%] right-[6%]">
                <ul className="grid h-full grid-cols-4 grid-rows-3 gap-x-0 gap-y-10 lg:px-10">
                  {STAFF.map((staff, i) => {
                    const isActive = i === active;
                    const iconSrc = `/images/staff-icon-${String(i + 1).padStart(2, '0')}.png`;
                    return (
                      <li key={i} className="flex items-start justify-center">
                        <button
                          type="button"
                          onClick={() => setActive(i)}
                          aria-pressed={isActive}
                          aria-label={`Staff ${i + 1}: ${t(staff.nameKey)}`}
                          className={`group relative flex w-full max-w-[3.5rem] flex-col items-center transition-transform duration-200 ease-out focus:outline-none sm:max-w-[4.5rem] md:max-w-[5.5rem] lg:max-w-[8.5rem] ${
                            isActive ? '' : 'hover:scale-105'
                          }`}
                        >
                          {/* Icon + pill background grouped in one
                              wrapper. The SVG provides the size
                              baseline (intrinsic 126×89 resting /
                              145×94 selected, scaled to cell width);
                              the icon is absolute-positioned over
                              the SVG with its body tucked inside the
                              pill and head sticking up above. Because
                              both children live inside this single
                              wrapper, the active-state `scale-110`
                              transform applies to the pair as a unit
                              — the icon never drifts off the pill
                              when the cell pops up, regardless of
                              cell size. Resting pill = Figma 186:4709
                              (cream→peach gradient). Selected pill =
                              Figma 186:4700 (asymmetric rose-pink). */}
                          <div
                            className={`relative w-full origin-top transition-transform duration-200 ease-out ${
                              isActive ? 'scale-110' : ''
                            }`}
                          >
                            <img
                              src={
                                isActive
                                  ? '/images/staff-bg-selected.svg'
                                  : '/images/staff-item-bg.svg'
                              }
                              alt=""
                              aria-hidden="true"
                              draggable={false}
                              className="block h-auto w-full select-none"
                            />
                            <PlaceholderImage
                              src={iconSrc}
                              alt={`Staff ${i + 1} portrait`}
                              hint={`#${i + 1}`}
                              className="pointer-events-none absolute left-1/2 bottom-[15%] z-10 h-[85%] w-auto -translate-x-1/2 object-contain"
                            />
                          </div>

                          {/* Name label — selected gets a white
                              rounded-pill background per Figma
                              186:4534, resting is plain centred
                              text. line-clamp-2 keeps long names
                              from pushing the cell out of its row. */}
                          <p
                            className={`hidden paw-label no-wrap min-h-[3.5rem] xl:block mt-0 line-clamp-2 max-w-full text-center font-thai text-[0.6rem] font-bold leading-tight transition md:mt-0 md:text-xs lg:text-lg ${
                              isActive
                                ? 'rounded-full text-section-feature shadow-sm'
                                : 'text-section-feature'
                            } px-2 py-0.5 md:px-3 md:py-1`}
                          >
                            {t(staff.nameKey)}
                          </p>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          {/* Featured character column — sibling of the frame on the
              right. Stacked on mobile (below the frame), flex-row on
              md+. Contains four layered elements:
                1. The ellipse "stage" — two pale circles behind the
                   character to mimic the spotlight backdrop from
                   Figma 1:4178.
                2. The character portrait itself.
                3. A speech bubble overlapping the top.
                4. MBTI + name pills along the bottom, matching
                   Figma 1:4181 / 1:4184.

              Mobile sizing (rule 3): `flex-1 min-h-0` lets this
              column eat all the vertical space the Reveal has left
              after the frame wrapper claims its 45vh — i.e.
              everything between the title pill and the frame
              becomes "staff display" room. `md:flex-initial`
              restores the original desktop sizing where the column
              is just a 22%-wide, 60vh-tall fixed lane on the right
              of the frame. */}
          {/* `hidden md:flex` — display:none on mobile (the new
              in-wrapper card above takes over there), then
              display:flex from md+. Previously this carried
              `hidden ... flex` (both unconditional); Tailwind's
              source order puts `hidden` after `flex`, so `hidden`
              won at EVERY breakpoint and the column never
              rendered. `md:flex-initial` overrides the now-mostly-
              irrelevant `flex-1` once we're on desktop, restoring
              the original 22%-wide / 60vh-tall fixed lane to the
              right of the frame. */}
          <div
            key={active}
            className="hidden md:flex pb-2 2xl:pb-4 relative max-w-[30%] h-full flex-col items-center md:shrink-0"
          >
            {/* Character + ellipse stage. The two pale rose ellipses
                form a "spotlight" backdrop behind the chibi
                portrait; they're rendered as pure CSS so they
                scale with the column without a separate asset
                round-trip.
                The speech bubble now lives INSIDE this stage so it
                can position itself against the stage's box edges
                (which coincide with the PlaceholderImage's box
                edges via `h-full w-full`). Specifically the bubble
                uses `absolute bottom-full left-0`, which sets its
                BOTTOM edge to `100%` from the stage's bottom — i.e.
                exactly at the stage's TOP edge — making the
                bubble's bottom physically stick to the top of the
                PlaceholderImage. Because the bubble is absolutely
                positioned, it extends ABOVE the stage's box; the
                column has no `overflow-hidden`, so the bubble is
                free to render in the space above the stage where
                it used to live as a flex sibling. */}
            <div className="relative flex w-full flex-1 items-end justify-center">
              {/* Speech bubble — the bubble silhouette + tail are
                  baked into staff-chat-bubble.png, so this wrapper
                  drops the previous bg-white / shadow / rounded-2xl
                  / tail-span chrome and just paints the asset as a
                  stretched background. `backgroundSize: 100% 100%`
                  lets the bubble grow with the text content while
                  preserving the rounded shape. Padding accounts for
                  the visible bubble border baked into the PNG.
                  `absolute bottom-full left-0` anchors the bubble's
                  bottom edge to the stage's top edge — equivalent
                  to "bubble's bottom sits on top of the character
                  image" since the image is `h-full w-full` inside
                  the stage. `z-10` keeps the bubble above the
                  spotlight circles below in case the bubble's box
                  ever overlaps them (e.g. on very short stages). */}
              
              {/* Top spotlight circle */}
              <div
                aria-hidden="true"
                className="absolute left-1/2 top-[5%] -z-0 aspect-square w-[88%] -translate-x-1/2 rounded-full "
              />
              {/* Ground shadow ellipse */}
              <div
                aria-hidden="true"
                className="absolute bottom-[18%] left-1/2 -z-0 h-[15%] w-[60%] -translate-x-1/2 rounded-full bg-section-feature/20 blur-[2px]"
              />
              {/* `h-full w-full object-contain` lets the portrait
                  scale with its parent stage. The stage is `flex-1`
                  inside the column, so the portrait absorbs all the
                  column's leftover vertical space (column height −
                  MBTI/name row), and `object-contain` keeps the
                  portrait's intrinsic aspect ratio inside that
                  stretchy box. */}
              <div className="relative flex flex-col justify-end items-center my-auto z-[1] h-auto w-full min-w-[28vw] min-h-[40vh] xl:min-w-[28vw] xl:min-h-[61vh] 2xl:min-h-[70vh]">
                <img
                  src={'/images/staff/staff-bg-light.png'}
                  className="absolute top-0 left-0 z-[1] h-full w-full object-contain"
                />
                <PlaceholderImage
                  src={current.fullSrc}
                  alt={`Staff ${active + 1} full`}
                  hint={`staff ${active + 1} full`}
                  className="relative z-[1] h-auto w-full object-contain"
                />
                <div
                  className="absolute translate-y-[-100%] top-8 lg:top-10 2xl:top-14 left-0 z-10 inline-flex md:max-w-[100%] lg:max-w-[75%] 2xl:max-w-[55%] items-center justify-center whitespace-pre-line px-6 py-3 text-center font-thai text-xs font-bold leading-snug text-section-feature md:px-8 md:py-4 md:text-sm lg:text-base"
                  style={{
                    backgroundImage: "url('/images/staff-chat-bubble.png')",
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                  }}
                >
                  {t(current.speechKey)}
                </div>

                <div className="mt-2  flex w-full items-center justify-center gap-2">
              <span
                className="rounded-full border-[6px] border-section-feature bg-[#ffb9b9] px-3 py-1 font-display text-xs font-extrabold leading-none text-section-feature md:px-4 md:py-1.5 md:text-xl lg:text-2xl"
                style={{
                  // Same extruded brown shadow as
                  // .register-title-shadow — three stacked
                  // drop-shadows trace the pill+border silhouette
                  // (filter: drop-shadow follows the alpha mask of
                  // the rendered element, so the offset copies
                  // include the pill border, not just the text
                  // glyphs).
                  filter:
                    'drop-shadow(0 1px 0 #634645) drop-shadow(0 1px 0 #59220d) drop-shadow(0 1px 0 #59220d)',
                  // 3px white stroke around the MBTI glyphs.
                  // `paintOrder: stroke fill` keeps the stroke
                  // *behind* the brown fill so the white reads as
                  // a halo around each letter rather than
                  // overpowering the brown.
                  WebkitTextStroke: '3px #FFFFFF',
                  paintOrder: 'stroke fill',
                }}
              >
                {current.mbti}
              </span>
              <span className="flex truncate rounded-full bg-section-feature px-4 py-1.5 text-center font-thai text-xs font-bold text-white md:py-2 md:text-sm lg:text-base">
                {t(current.nameKey)}
              </span>
            </div>
              </div>
            </div>

            {/* MBTI + Name pills row — anchored to the bottom of the
                column, matching Figma 1:4181 (MBTI) + 1:4184
                (Name). The MBTI pill is a compact pale-pink pellet
                with a brown ring; the name pill is the longer
                dark-brown pill with the character's full name. */}
            
          </div>
        </Reveal>

        {/* Bottom decorative fork-spoon strip — Figma 1:4191
            ("Top Line (cooking)"). Twelve fork-spoon icons tiled
            edge-to-edge across the full width of the outer content
            panel, sitting between the staff content and the next
            section. */}
        <div
          aria-hidden="true"
          className="hidden lg:block h-[180px] w-[90%] bg-[url('/images/fork-spoon.svg')] bg-[length:72px_24px] bg-center bg-repeat-x opacity-70"
        />
        
      </div>

    </section>
  );
}

export { STAFF_COUNT };
